/**
 * The work timeline: one lane per agent (or repository), a rounded bar per
 * run coloured by what set it going, a Now line, a minimap of the whole
 * range under the lanes with the zoomed window on it, and a footer that
 * says how many runs over what range and lets the range be changed.
 *
 * The layout maths is all in ./workTimelineModel.ts; this draws it. A bar's
 * hover says what it was in a line; a click (or Enter on the selected bar)
 * opens its details — what it was asked, who set it going, the task, plan
 * and roadmap items it served — with the way into each. The arrows move the
 * selection between bars, Escape closes the details. Shift and the wheel (or
 * a sideways swipe) pan, Ctrl and the wheel zoom about the pointer, and the
 * minimap's window can be dragged. Past 40 lanes only those in view are
 * drawn, so a project of two hundred agents scrolls as smoothly as one of
 * eight.
 *
 * Presentational: the runs come in as props (useWorkTimeline.ts reads them).
 */
import './workTimeline.css';

import { ArrowUpRightFromSquare, BarsAscendingAlignLeft, CircleDollar, Cpu, Ellipsis, Folder, MagnifierMinus, MagnifierPlus, PersonGear, Persons, Play, ScalesBalanced, Stopwatch, Xmark } from '@gravity-ui/icons';
import { Button, DateField, DateRangePicker, Dropdown, Label, RangeCalendar } from '@heroui/react';
import { I18nProvider } from 'react-aria-components';
import { Segment } from '@heroui-pro/react';
import { type CalendarDate, parseDate } from '@internationalized/date';
import { type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type WheelEvent as ReactWheelEvent } from 'react';

import type { RunTotals } from '@aioe/shared';
import { useDialogFocus } from '../../workbench/useDialogFocus.ts';
import { usePlanTone } from '../../workbench/usePlanTone.ts';
import { planToneAttr } from '../../workbench/planTones.ts';
import { type ArtMotif, ArtStat, type ArtTone, VIEW_TONES } from '../ui/Art.tsx';
import { FitText } from '../ui/FitText.tsx';
import { Hint } from '../ui/Hint.tsx';
import { LoadingState } from '../ui/LoadingState.tsx';
import { type OpenResult, useTimelineOpen } from './timelineOpen.tsx';
import { type CommitOverlayInput, useCommitOverlay } from './useCommitOverlay.tsx';
import {
  axisTicks,
  barGeometry,
  barLook,
  barPlanTone,
  barSummary,
  barTop,
  buildLanes,
  centreMinimapAt,
  commitMarkerY,
  COMPACT_METRICS,
  dragMinimap,
  endOfDay,
  footerText,
  isRebuiltRun,
  LANE_METRICS,
  laneHeight,
  laneOffsets,
  type LanesBy,
  minimapWindow,
  moveSelection,
  panWindow,
  placeLabels,
  PLAN_LEGEND_MAX,
  planLegend,
  type PlanLegendItem,
  RANGE_PRESETS,
  type RangePreset,
  rangeSegmentKey,
  REBUILT_NOTE,
  runDetails,
  runLegend,
  type RunLegendItem,
  runSpan,
  startOfDay,
  type TimelineKpi,
  timelineKpis,
  type TimelineLane,
  type TimelineRun,
  timeScale,
  type TimeWindow,
  totalsOf,
  visibleLaneRange,
  zoomLevel,
  zoomWindow,
} from './workTimelineModel.ts';

export interface WorkTimelineProps {
  runs: readonly TimelineRun[];
  /** The numbers over `runs`; worked out here when absent (a chip cut the runs). */
  totals?: RunTotals;
  from: number;
  to: number;
  /** "Now" for a run still going; the Now line shows when it is inside the range. */
  now: number;
  lanesBy: LanesBy;
  loading?: boolean;
  error?: string;
  /** Ask again, from the loading or failure state (D85). */
  onRetry?: () => void;
  /** The range control in the footer; without it the footer only says the range. */
  onRange?: (from: number, to: number, preset: RangePreset) => void;
  preset?: RangePreset;
  /** A run was selected (clicked, or Enter on it). */
  onSelect?: (run: TimelineRun) => void;
  /** What this timeline is cut by, in words: "Plan PLAN-3", "Kai in the core team". */
  filterLabel?: string;
  /** For embedding: no figures, smaller lanes, a lower box. */
  compact?: boolean;
  /** Inside another view (Plans, Roadmap): the figures as smaller cards. */
  smallStats?: boolean;
  /** Console: no figures, where the view above already has its own (History). */
  hideStats?: boolean;
  /** Console: "Read the runs again", in the toolbar's More menu. */
  onRefresh?: () => void;
  /** Console: the commit overlay's "Show commits" switch, in the toolbar's More menu. */
  commitsToggle?: { on: boolean; toggle: () => void };
  /** The second line of an agent lane names its repository too (the whole project). */
  showRepository?: boolean;
  opsManagers?: ReadonlySet<string>;
  /** What the empty timeline says. */
  emptyText?: string;
  /** Beside the zoom buttons: a lanes toggle, a refresh. */
  actions?: ReactNode;
  /** After the zoom buttons, in the toolbar's corner: the full-screen control. */
  corner?: ReactNode;
  /** The lanes' box at most this tall (px) before it scrolls. */
  maxLanesHeight?: number;
  /** Hide the open-in actions (the view that embeds it is already that thing). */
  hideOpen?: Partial<Record<'task' | 'plan' | 'roadmap', boolean>>;
  /** Open zoomed to this part of the range (a plan's own active period); Reset shows the whole range. */
  fitWindow?: TimeWindow;
  /** The commit overlay (D180): markers in the lanes, lanes for people and AI outside the workbench, its legend beside the runs'. */
  commits?: CommitOverlayInput;
}

const AXIS_H = 26;
const MINIMAP_H = 26;

/**
 * Each figure's card: its own icon, and a tone borrowed from the view the
 * figure is about — runs the Timeline's, agents Teams', run time Routines',
 * tokens Chat's, cost Source Control's — so the five read apart at a glance.
 * The words are in timelineKpis (workTimelineModel.ts).
 */
const KPI_LOOK: Record<TimelineKpi['id'], { tone: ArtTone; motif: ArtMotif; icon: ReactNode }> = {
  // Console: the console's own areas lend the tones (Activity, Teams, Costs); run time and tokens keep the workbench's.
  runs: { tone: VIEW_TONES.activity!, motif: 'rings', icon: <Play /> },
  agents: { tone: VIEW_TONES.teams!, motif: 'arc', icon: <Persons /> },
  runTime: { tone: 'amber', motif: 'dots', icon: <Stopwatch /> },
  tokens: { tone: 'teal', motif: 'rings', icon: <Cpu /> },
  cost: { tone: VIEW_TONES.costs!, motif: 'arc', icon: <CircleDollar /> },
};

function LaneGlyph({ lane, by }: { lane: TimelineLane; by: LanesBy }) {
  const title = by === 'repository' ? 'Repository' : lane.ops ? 'Operations manager: plans, delegates, reviews' : 'Agent';
  return (
    <span className="wtl-lane__glyph" data-ops={lane.ops || undefined} title={title} aria-hidden="true">
      {by === 'repository' ? <Folder /> : lane.ops ? <ScalesBalanced /> : <PersonGear />}
    </span>
  );
}

const LEGEND_TEXT: Record<RunLegendItem, string> = { asked: 'Asked or handed over', automation: 'Automation', cancelled: 'Cancelled', failed: 'Failed', rebuilt: 'Rebuilt', now: 'Now' };

function Legend({ items, plans }: { items: readonly RunLegendItem[]; plans: readonly PlanLegendItem[] }) {
  // Two or more plans running: their bars are in their plans' colours, so the legend names those plans first
  // (D178); solid and striped still tell a person's ask from automation, in a neutral swatch. Only what is
  // drawn in the window is listed (runLegend), so the legend never names a bar that is not there.
  const shown = plans.slice(0, PLAN_LEGEND_MAX);
  const more = plans.length - shown.length;
  if (!items.length && !shown.length) return null;
  return (
    <ul className="wtl-legend" aria-label="Legend" data-wtl-legend data-plans={plans.length ? true : undefined}>
      {shown.map((p) => (
        <li key={p.planId} data-wtl-legend-plan={p.planId}>
          <span className="wtl-swatch" {...planToneAttr(p.colorIndex)} aria-hidden="true" /> {p.label}
        </li>
      ))}
      {more > 0 ? <li className="text-muted">+{more} more plans</li> : null}
      {items.map((it) => (
        <li key={it} data-wtl-legend-item={it}>
          <span className="wtl-swatch" data-look={it} aria-hidden="true" /> {LEGEND_TEXT[it]}
        </li>
      ))}
    </ul>
  );
}

const two = (n: number) => String(n).padStart(2, '0');
const dayOf = (t: number): CalendarDate => parseDate(`${new Date(t).getFullYear()}-${two(new Date(t).getMonth() + 1)}-${two(new Date(t).getDate())}`);
const msOf = (d: CalendarDate): number => new Date(d.year, d.month - 1, d.day).getTime();

/** Today · 7 days · 30 days · from/to: exactly the range control the reference had. */
function RangeControl({ from, to, now, preset, onRange }: { from: number; to: number; now: number; preset: RangePreset; onRange: (from: number, to: number, preset: RangePreset) => void }) {
  const value = { start: dayOf(from), end: dayOf(Math.max(from, to - 1)) };
  return (
    <span className="ml-auto flex flex-wrap items-center gap-2" data-wtl-range>
      <span>Range:</span>
      <Segment
        aria-label="Range"
        size="sm"
        // A custom range highlights nothing (or the preset it is exactly); a null key would keep the last highlight.
        selectedKey={rangeSegmentKey(preset, from, to, now)}
        onSelectionChange={(key) => {
          const id = String(key) as Exclude<RangePreset, 'custom'>;
          if (!RANGE_PRESETS.some((p) => p.id === id)) return;
          onRange(0, 0, id);
        }}
      >
        {RANGE_PRESETS.map((p) => (
          <Segment.Item key={p.id} id={p.id}>
            {p.label}
          </Segment.Item>
        ))}
      </Segment>
      {/* Console: Australian dates (day, month, year) whatever the browser's own locale. */}
      <I18nProvider locale="en-AU">
        <DateRangePicker
          aria-label="From and to"
          className="w-[15.5rem]"
          value={value}
          onChange={(range) => {
            if (!range) return;
            onRange(startOfDay(msOf(range.start)), endOfDay(msOf(range.end)), 'custom');
          }}
        >
          <DateField.Group fullWidth variant="secondary">
            <DateField.Input slot="start">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
            <DateRangePicker.RangeSeparator />
            <DateField.Input slot="end">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
            <DateField.Suffix>
              <DateRangePicker.Trigger>
                <DateRangePicker.TriggerIndicator />
              </DateRangePicker.Trigger>
            </DateField.Suffix>
          </DateField.Group>
          <DateRangePicker.Popover>
            <RangeCalendar aria-label="From and to">
              <RangeCalendar.Header>
                <RangeCalendar.YearPickerTrigger>
                  <RangeCalendar.YearPickerTriggerHeading />
                  <RangeCalendar.YearPickerTriggerIndicator />
                </RangeCalendar.YearPickerTrigger>
                <RangeCalendar.NavButton slot="previous" />
                <RangeCalendar.NavButton slot="next" />
              </RangeCalendar.Header>
              <RangeCalendar.Grid>
                <RangeCalendar.GridHeader>{(day) => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}</RangeCalendar.GridHeader>
                <RangeCalendar.GridBody>{(date) => <RangeCalendar.Cell date={date} />}</RangeCalendar.GridBody>
              </RangeCalendar.Grid>
              <RangeCalendar.YearPickerGrid>
                <RangeCalendar.YearPickerGridBody>{({ year }) => <RangeCalendar.YearPickerCell year={year} />}</RangeCalendar.YearPickerGridBody>
              </RangeCalendar.YearPickerGrid>
            </RangeCalendar>
          </DateRangePicker.Popover>
        </DateRangePicker>
      </I18nProvider>
    </span>
  );
}

function Details({ run, now, left, top, hideOpen, onClose }: { run: TimelineRun; now: number; left: number; top: number; hideOpen?: WorkTimelineProps['hideOpen']; onClose: () => void }) {
  const d = runDetails(run, now);
  // Console: every repository's runs and their graphs are the project's here (./timelineOpen.tsx).
  const { openRoadmapItem, openRunPlan, openRunTask, showActivity, shownRun } = useTimelineOpen();
  const [note, setNote] = useState<string | undefined>(undefined);
  const act = (p: Promise<OpenResult>) => void p.then((r) => setNote(r.ok ? undefined : r.message));
  const ref = useRef<HTMLDivElement>(null);
  // Focus inside on open, Escape closes, focus back to the timeline after.
  useDialogFocus(ref, { onEscape: onClose });
  return (
    <div
      ref={ref}
      className="wtl-details"
      style={{ left, top }}
      role="dialog"
      aria-label={`Run: ${d.title}`}
      tabIndex={-1}
      data-wtl-details={run.id}
    >
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground">{d.title}</span>
        <Hint text="Close (Esc)">
          <Button size="sm" variant="ghost" isIconOnly aria-label="Close the details" className="-mr-1 -mt-1" onPress={onClose}>
            <Xmark className="size-3.5" />
          </Button>
        </Hint>
      </div>
      <dl>
        <dt>Agent</dt>
        <dd>
          {run.agent}
          {d.model ? <span className="text-muted"> · {d.model}</span> : null}
        </dd>
        <dt>Set going</dt>
        <dd>{d.trigger}</dd>
        <dt>Outcome</dt>
        <dd>
          {d.outcome} · {d.duration}
          {/* D312: the turn moved down its class's fallback chain — said once, muted. */}
          {d.finishedOn ? <span className="block text-muted" data-run-finished-on>{d.finishedOn}</span> : null}
        </dd>
        <dt>When</dt>
        <dd className="tabular-nums">{d.when}</dd>
        <dt>Tokens used</dt>
        <dd className="tabular-nums">
          {d.tokens}
          {d.cost ? ` · ${d.cost}` : ''}
        </dd>
        {d.rebuilt ? (
          <>
            <dt>Record</dt>
            <dd>Rebuilt from the board and agent history; times are approximate</dd>
          </>
        ) : null}
        <dt>Repository</dt>
        <dd>{d.repository}</dd>
        <dt>Team</dt>
        <dd>{d.team}</dd>
        {d.task ? (
          <>
            <dt>Task</dt>
            <dd>{d.task}</dd>
          </>
        ) : null}
        {d.plan ? (
          <>
            <dt>Plan</dt>
            <dd>{d.plan}</dd>
          </>
        ) : null}
        {d.roadmap.length ? (
          <>
            <dt>Roadmap</dt>
            <dd className="flex flex-col gap-0.5">
              {d.roadmap.map((r) => (
                <span key={r.key} className="flex flex-wrap items-baseline gap-x-1.5">
                  {hideOpen?.roadmap ? (
                    <span>{r.text}</span>
                  ) : (
                    <button type="button" className="cursor-[var(--cursor-interactive)] border-none bg-transparent p-0 text-left text-xs text-foreground underline decoration-foreground/30 underline-offset-2 hover:text-accent" onClick={() => act(openRoadmapItem(run, r))} data-wtl-open-roadmap={r.id}>
                      {r.text}
                    </button>
                  )}
                  {r.delegated ? <span className="text-muted">via delegation</span> : null}
                </span>
              ))}
            </dd>
          </>
        ) : null}
      </dl>
      {/* Console: the run as a graph (D240) is History's activity graph, under the timeline. */}
      {showActivity || (run.task && !hideOpen?.task) || (run.plan && !hideOpen?.plan) ? (
        <div className="flex flex-wrap gap-1.5">
          {showActivity ? (
            <Button size="sm" variant={shownRun === run.id ? 'secondary' : 'primary'} onPress={() => showActivity(run)} data-wtl-show-activity>
              {shownRun === run.id ? 'Showing its activity' : 'Show its activity'}
            </Button>
          ) : null}
          {run.task && !hideOpen?.task ? (
            <Button size="sm" variant="secondary" onPress={() => act(openRunTask(run))} data-wtl-open-task>
              <ArrowUpRightFromSquare className="size-3" /> Open task
            </Button>
          ) : null}
          {run.plan && !hideOpen?.plan ? (
            <Button size="sm" variant="ghost" onPress={() => act(openRunPlan(run))} data-wtl-open-plan>
              <ArrowUpRightFromSquare className="size-3" /> Open plan
            </Button>
          ) : null}
        </div>
      ) : null}
      {note ? (
        <p className="m-0 text-xs text-danger" role="alert">
          {note}
        </p>
      ) : null}
    </div>
  );
}

function useWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setWidth(el.clientWidth);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export function WorkTimeline(props: WorkTimelineProps) {
  const { runs, from, to, now, lanesBy, loading, error, onRetry, onRange, preset = 'custom', onSelect, filterLabel, compact, smallStats, hideStats, onRefresh, commitsToggle, showRepository, opsManagers, emptyText, actions, corner, maxLanesHeight, hideOpen, fitWindow } = props;
  const m = compact ? COMPACT_METRICS : LANE_METRICS;
  const full = useMemo<TimeWindow>(() => ({ start: from, end: to }), [from, to]);
  const fullKey = `${from}|${to}`;
  const [zoom, setZoom] = useState<{ for: string; win: TimeWindow } | undefined>(undefined);
  // Until the person zooms, a timeline given a fit opens on it (clamped to the range).
  const fitStart = fitWindow?.start;
  const fitEnd = fitWindow?.end;
  const opening = useMemo<TimeWindow>(() => {
    if (fitStart === undefined || fitEnd === undefined) return full;
    const start = Math.max(full.start, fitStart);
    const end = Math.min(full.end, fitEnd);
    return end > start ? { start, end } : full;
  }, [full, fitStart, fitEnd]);
  const win = zoom && zoom.for === fullKey ? zoom.win : opening;
  const setWin = (next: TimeWindow) => setZoom({ for: fullKey, win: next });

  const totals = useMemo(() => props.totals ?? totalsOf(runs, now), [props.totals, runs, now]);
  // Plans in colour (D178): the running plans' bars take their plan's colour; a crew's lane its plan's dot.
  const toneOf = usePlanTone();
  const lanes = useMemo(() => buildLanes(runs, lanesBy, now, { opsManagers, showRepository, planToneOf: (id) => toneOf(id) }), [runs, lanesBy, now, opsManagers, showRepository, toneOf]);
  const plansShown = useMemo(() => planLegend(runs, (id) => toneOf(id), now), [runs, now, toneOf]);
  const byId = useMemo(() => new Map(runs.map((r) => [r.id, r])), [runs]);

  const [chartRef, chartWidth] = useWidth();
  const [labelRef, labelWidth] = useWidth();
  const plotWidth = Math.max(0, chartWidth - labelWidth);
  // The commit overlay (./CommitOverlay.tsx): its lanes go below the run lanes; the run lanes are unchanged.
  const overlay = useCommitOverlay(props.commits, { lanes, lanesBy, win, plotWidth, chartRef, chartWidth, showRepository });
  const shownLanes = overlay.lanes;
  const offsets = useMemo(() => laneOffsets(shownLanes, m), [shownLanes, m]);
  const scale = timeScale(win, plotWidth);
  const ticks = useMemo(() => axisTicks(win, plotWidth), [win, plotWidth]);
  const nowShown = now >= win.start && now <= win.end;

  const lanesRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const lanesMax = maxLanesHeight ?? (compact ? 180 : 440);
  const viewport = Math.min(lanesMax, offsets.total);
  const range = visibleLaneRange(offsets.tops, offsets.total, scrollTop, viewport || lanesMax);

  const [hover, setHover] = useState<{ id: string; x: number; y: number } | undefined>(undefined);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hatchId = `wtl-hatch-${useId().replace(/:/g, '')}`;

  const selectedRun = selected ? byId.get(selected) : undefined;
  const select = (id: string | undefined, open: boolean) => {
    setSelected(id);
    setDetailsOpen(open && !!id);
    const run = id ? byId.get(id) : undefined;
    if (run && open) onSelect?.(run);
    // Keep the selected bar in view: scroll its lane in, and pan the window to it.
    if (!run) return;
    const li = shownLanes.findIndex((l) => l.runs.some((r) => r.id === run.id));
    const el = lanesRef.current;
    if (li >= 0 && el) {
      const top = offsets.tops[li];
      const h = laneHeight(shownLanes[li], m);
      if (top < el.scrollTop) el.scrollTop = top;
      else if (top + h > el.scrollTop + el.clientHeight) el.scrollTop = top + h - el.clientHeight;
    }
    const span = runSpan(run, now);
    if (span.end < win.start || span.start > win.end) setWin(panWindow(win, full, span.start - (win.start + win.end) / 2 + (span.end - span.start) / 2));
  };

  // Where the details card goes: under the selected bar, kept inside the card.
  const anchor = (() => {
    if (!selectedRun || !detailsOpen) return undefined;
    const li = shownLanes.findIndex((l) => l.runs.some((r) => r.id === selectedRun.id));
    if (li < 0) return undefined;
    const lane = shownLanes[li];
    const g = barGeometry(selectedRun, win, plotWidth, now);
    const row = lane.rowOf.get(selectedRun.id) ?? 0;
    const top = offsets.tops[li] - scrollTop + barTop(row, m);
    const cardW = Math.min(336, chartWidth - 16);
    const x = labelWidth + (g ? g.x : 0);
    const left = Math.max(8, Math.min(chartWidth - cardW - 8, x));
    const below = AXIS_H + top + m.barHeight + 6;
    return { left, top: Math.max(AXIS_H, below) };
  })();

  // Escape closes the details from anywhere in the timeline.
  useEffect(() => {
    if (!detailsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Taken: a full-screen timeline closes its details first, and stays full screen.
      e.preventDefault();
      setDetailsOpen(false);
    };
    // Captured: a focused button's tooltip would otherwise take Escape for itself.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [detailsOpen]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      select(moveSelection(lanes, selected, e.key, now), false);
    } else if (e.key === 'Enter' && selected) {
      e.preventDefault();
      select(selected, true);
    } else if (e.key === 'Escape') {
      setDetailsOpen(false);
    } else if (e.key === '+' || e.key === '=') {
      setWin(zoomWindow(win, full, 2));
    } else if (e.key === '-') {
      setWin(zoomWindow(win, full, 0.5));
    } else if (e.key === '0') {
      setWin(full);
    }
  };

  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (!plotWidth) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const anchorT = scale.t(e.clientX - rect.left - labelWidth);
      setWin(zoomWindow(win, full, e.deltaY < 0 ? 1.25 : 0.8, anchorT));
      return;
    }
    const sideways = e.shiftKey ? e.deltaY || e.deltaX : Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : 0;
    if (sideways && zoomLevel(win, full) > 1.0001) {
      setWin(panWindow(win, full, sideways / Math.max(1e-9, scale.pxPerMs)));
    }
  };

  // Ctrl+wheel must not zoom the page: a passive React listener cannot prevent it, so one native listener does.
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) e.preventDefault();
    };
    el.addEventListener('wheel', stop, { passive: false });
    return () => el.removeEventListener('wheel', stop);
  }, [chartRef]);

  // The minimap: click to centre, drag the window to pan.
  const drag = useRef<{ x: number; win: TimeWindow } | undefined>(undefined);
  const mini = minimapWindow(win, full, plotWidth);
  const miniScale = timeScale(full, plotWidth);
  const onMiniDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let start = win;
    if (x < mini.x || x > mini.x + mini.w) {
      start = centreMinimapAt(win, full, plotWidth, x);
      setWin(start);
    }
    drag.current = { x: e.clientX, win: start };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMiniMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag.current) return;
    setWin(dragMinimap(drag.current.win, full, plotWidth, e.clientX - drag.current.x));
  };
  const onMiniUp = () => {
    drag.current = undefined;
  };

  const zoomed = zoomLevel(win, full) > 1.0001;
  const legendItems = useMemo(() => runLegend(runs, win, now), [runs, win, now]);
  const hasRebuilt = runs.some((r) => isRebuiltRun(r));
  const kpis = timelineKpis(totals, runs);
  const hoverRun = hover ? byId.get(hover.id) : undefined;
  const empty = !runs.length && !overlay.count;

  return (
    <section className="wtl @container" data-compact={compact || undefined} data-work-timeline data-lanes-by={lanesBy}>
      {!compact && !hideStats ? (
        <div className="art-stats wtl-stats" data-size={smallStats ? 'small' : undefined} data-wtl-kpis>
          {kpis.map((k) => {
            const look = KPI_LOOK[k.id];
            return (
              // `contents`: the card is the grid's item; the wrapper only names it for tests and the harness.
              <div key={k.id} className="contents" data-wtl-kpi={k.id}>
                <ArtStat tone={look.tone} motif={look.motif} icon={look.icon} label={k.label} value={loading && !runs.length ? undefined : k.value} detail={k.detail} />
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="wtl-card" tabIndex={0} role="group" aria-label={`Work timeline${filterLabel ? `: ${filterLabel}` : ''}. Arrow keys move between runs, Enter opens one.`} onKeyDown={onKeyDown}>
        <div className="wtl-toolbar">
          {filterLabel ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-foreground" data-wtl-filter>
              <BarsAscendingAlignLeft className="size-3.5 shrink-0 text-muted" aria-hidden="true" />
              <FitText>{filterLabel}</FitText>
            </span>
          ) : null}
          <Legend items={legendItems} plans={plansShown} />
          {overlay.legend}
          <span className="flex-1" />
          {actions}
          <span className="inline-flex items-center gap-0.5" data-wtl-zoom>
            <Hint text="Zoom out (−)">
              <Button size="sm" variant="ghost" isIconOnly aria-label="Zoom out" isDisabled={!zoomed} onPress={() => setWin(zoomWindow(win, full, 0.5))}>
                <MagnifierMinus className="size-3.5" />
              </Button>
            </Hint>
            <Hint text="Zoom in (+)">
              <Button size="sm" variant="ghost" isIconOnly aria-label="Zoom in" isDisabled={win.end - win.start <= 10 * 60_000 + 1} onPress={() => setWin(zoomWindow(win, full, 2, nowShown ? now : undefined))}>
                <MagnifierPlus className="size-3.5" />
              </Button>
            </Hint>
          </span>
          {/* Console: Reset and Refresh in one More menu, so the toolbar keeps to three controls (lanes, zoom, More). */}
          <Dropdown>
            <Hint text="More">
              <Button size="sm" variant="ghost" isIconOnly aria-label="More for the timeline" data-wtl-more>
                <Ellipsis className="size-3.5" />
              </Button>
            </Hint>
            <Dropdown.Popover placement="bottom end" className="min-w-52">
              <Dropdown.Menu aria-label="More for the timeline" disabledKeys={zoomed ? [] : ['reset']} onAction={(key) => (key === 'reset' ? setWin(full) : key === 'refresh' ? onRefresh?.() : key === 'commits' ? commitsToggle?.toggle() : undefined)}>
                <Dropdown.Item id="reset" textValue="Show the whole range" data-wtl-zoom-reset>
                  <Label>Show the whole range</Label>
                </Dropdown.Item>
                {commitsToggle ? (
                  <Dropdown.Item id="commits" textValue={commitsToggle.on ? 'Hide the commits' : 'Show the commits'} data-wtl-show-commits={commitsToggle.on}>
                    <Label>{commitsToggle.on ? 'Hide the commits' : 'Show the commits'}</Label>
                  </Dropdown.Item>
                ) : null}
                {onRefresh ? (
                  <Dropdown.Item id="refresh" textValue="Read the runs again" data-wtl-refresh>
                    <Label>Read the runs again</Label>
                  </Dropdown.Item>
                ) : null}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
          {corner}
        </div>

        {hasRebuilt ? (
          <p className="wtl-note" data-wtl-rebuilt-note>
            {REBUILT_NOTE}
          </p>
        ) : null}

        <div className="wtl-chart" ref={chartRef} onWheel={onWheel}>
          {/* A hidden pattern every lane's bars share: automation's hatch. */}
          <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
            <defs>
              <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="2.2" height="6" fill="rgb(255 255 255 / 0.34)" />
              </pattern>
            </defs>
          </svg>

          <div className="wtl-row wtl-axis" style={{ height: AXIS_H }}>
            <div className="wtl-axis__corner" ref={labelRef}>
              {lanesBy === 'agent' ? 'Agent' : 'Repository'}
            </div>
            <svg width={plotWidth} height={AXIS_H} aria-hidden="true">
              {ticks.map((t) => (
                <text key={t.t} x={t.x + 4} y={AXIS_H - 9} data-major={t.major || undefined}>
                  {t.label}
                </text>
              ))}
              {ticks.map((t) => (
                <line key={`l${t.t}`} className="wtl-grid" data-major={t.major || undefined} x1={t.x} x2={t.x} y1={AXIS_H - 6} y2={AXIS_H} />
              ))}
              {nowShown ? (
                <>
                  <line className="wtl-now" x1={scale.x(now)} x2={scale.x(now)} y1={8} y2={AXIS_H} />
                  <path className="wtl-now-cap" d={`M${scale.x(now) - 4.5},2 L${scale.x(now) + 4.5},2 L${scale.x(now)},8 Z`} />
                </>
              ) : null}
            </svg>
          </div>

          {empty && (loading || error) ? (
            <LoadingState
              variant={compact ? 'inline' : 'canvas'}
              className={compact ? 'wtl-empty' : undefined}
              skeleton="rows"
              label="Reading the runs…"
              error={error}
              failedTitle="The timeline could not be read"
              {...(onRetry ? { onRetry } : {})}
            />
          ) : empty ? (
            <div className="wtl-empty" data-wtl-empty>
              {emptyText ?? 'No agent worked in this range. Runs show here as agents start and stop.'}
            </div>
          ) : (
            <div className="wtl-lanes" ref={lanesRef} style={{ maxHeight: lanesMax }} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} data-wtl-lanes={shownLanes.length}>
              <div style={{ height: offsets.total, position: 'relative' }}>
                {shownLanes.slice(range.first, range.last + 1).map((lane, k) => {
                  const li = range.first + k;
                  const h = laneHeight(lane, m);
                  const bars = lane.runs
                    .map((r) => ({ run: r, g: barGeometry(r, win, plotWidth, now) }))
                    .filter((b): b is { run: TimelineRun; g: NonNullable<ReturnType<typeof barGeometry>> } => !!b.g);
                  const labels = compact
                    ? new Map<string, string>()
                    : new Map(
                        Array.from({ length: lane.rows }, (_, row) => placeLabels(bars.filter((b) => (lane.rowOf.get(b.run.id) ?? 0) === row).map((b) => ({ id: b.run.id, x: b.g.x, w: b.g.w, text: b.run.title ?? b.run.task?.title ?? '' })))).flatMap((mm) => [...mm]),
                      );
                  return (
                    <div key={lane.key} className="wtl-lane wtl-row" style={{ top: offsets.tops[li], height: h }} data-wtl-lane={lane.label}>
                      <div className="wtl-lane__label" title={`${lane.label} · ${lane.sub}`}>
                        {overlay.glyph(lane) ?? <LaneGlyph lane={lane} by={lanesBy} />}
                        <span className="min-w-0">
                          <span className="wtl-lane__name">
                            {lane.crew?.colorIndex !== undefined ? <span className="plan-dot wtl-lane__plan" {...planToneAttr(lane.crew.colorIndex)} title={`On ${lane.crew.planRef}`} aria-hidden="true" /> : null}
                            {lane.label}
                          </span>
                          {!compact || lane.rows > 1 ? <span className="wtl-lane__sub">{lane.sub}</span> : null}
                        </span>
                      </div>
                      <svg width={plotWidth} height={h} role="list" aria-label={`${lane.label}: ${lane.runs.length} ${lane.runs.length === 1 ? 'run' : 'runs'}`}>
                        {ticks.map((t) => (
                          <line key={t.t} className="wtl-grid" data-major={t.major || undefined} x1={t.x} x2={t.x} y1={0} y2={h} />
                        ))}
                        {bars.map(({ run, g }) => {
                          const row = lane.rowOf.get(run.id) ?? 0;
                          const y = barTop(row, m);
                          const look = barLook(run);
                          const r = Math.min(4, m.barHeight / 2, g.w / 2);
                          const label = labels.get(run.id);
                          const failW = run.outcome === 'error' ? Math.min(4, Math.max(2, g.w / 4)) : 0;
                          const tone = barPlanTone(run, plansShown);
                          return (
                            <g
                              key={run.id}
                              className="wtl-bar"
                              role="listitem"
                              aria-label={barSummary(run, now)}
                              data-look={look}
                              {...(typeof tone === 'number' ? planToneAttr(tone) : tone === 'none' ? { 'data-plan': 'none' } : {})}
                              data-cancelled={run.outcome === 'cancelled' || undefined}
                              data-live={run.live || undefined}
                              data-rebuilt={isRebuiltRun(run) || undefined}
                              data-selected={selected === run.id || undefined}
                              data-wtl-bar={run.id}
                              onPointerEnter={(e) => {
                                const rect = chartRef.current?.getBoundingClientRect();
                                if (rect) setHover({ id: run.id, x: e.clientX - rect.left, y: e.clientY - rect.top });
                              }}
                              onPointerMove={(e) => {
                                const rect = chartRef.current?.getBoundingClientRect();
                                if (rect) setHover({ id: run.id, x: e.clientX - rect.left, y: e.clientY - rect.top });
                              }}
                              onPointerLeave={() => setHover(undefined)}
                              onClick={() => select(run.id, true)}
                            >
                              {/* A bigger target than the mark. */}
                              <rect x={g.x - 2} y={y - 3} width={g.w + 4} height={m.barHeight + 6} fill="transparent" />
                              <rect className="wtl-bar__body" x={g.x} y={y} width={g.w} height={m.barHeight} rx={r} ry={r} />
                              {look === 'automation' && run.outcome !== 'cancelled' ? <rect x={g.x} y={y} width={g.w} height={m.barHeight} rx={r} ry={r} fill={`url(#${hatchId})`} pointerEvents="none" /> : null}
                              {failW ? <rect className="wtl-bar__fail" x={g.x + g.w - failW} y={y} width={failW} height={m.barHeight} rx={Math.min(r, failW / 2)} pointerEvents="none" /> : null}
                              {label ? (
                                <text className="wtl-bar__label" x={g.x + 5} y={y + m.barHeight / 2 + 3.5}>
                                  {label}
                                </text>
                              ) : null}
                            </g>
                          );
                        })}
                        {overlay.markers(lane, commitMarkerY(lane, m))}
                        {/* Over the markers: a commit made a moment ago never hides the Now line. */}
                        {nowShown ? <line className="wtl-now" x1={scale.x(now)} x2={scale.x(now)} y1={0} y2={h} pointerEvents="none" /> : null}
                      </svg>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!empty ? (
            <div className="wtl-row wtl-minimap" style={{ height: MINIMAP_H }} data-wtl-minimap>
              <div className="wtl-minimap__corner">{zoomed ? `×${zoomLevel(win, full).toFixed(zoomLevel(win, full) < 10 ? 1 : 0)}` : 'Whole range'}</div>
              <svg width={plotWidth} height={MINIMAP_H} onPointerDown={onMiniDown} onPointerMove={onMiniMove} onPointerUp={onMiniUp} onPointerCancel={onMiniUp} role="slider" aria-label="Visible part of the range" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(((win.start - full.start) / Math.max(1, full.end - full.start)) * 100)}>
                {lanes.flatMap((lane, li) =>
                  lane.runs.map((run) => {
                    const { start, end } = runSpan(run, now);
                    const x = miniScale.x(Math.max(start, full.start));
                    const w = Math.max(1.5, miniScale.x(Math.min(end, full.end)) - x);
                    const rows = Math.max(1, lanes.length);
                    const y = 4 + ((li % rows) / rows) * (MINIMAP_H - 10);
                    return <rect key={run.id} className="wtl-minimap__tick" data-look={barLook(run)} data-rebuilt={isRebuiltRun(run) || undefined} x={x} y={y} width={w} height={Math.max(1.5, (MINIMAP_H - 10) / rows)} opacity={run.outcome === 'cancelled' ? 0.4 : 0.85} />;
                  }),
                )}
                {nowShown || (now >= full.start && now <= full.end) ? <line className="wtl-now" x1={miniScale.x(now)} x2={miniScale.x(now)} y1={2} y2={MINIMAP_H - 2} /> : null}
                {zoomed ? (
                  <>
                    <rect className="wtl-minimap__shade" x={0} y={0} width={mini.x} height={MINIMAP_H} />
                    <rect className="wtl-minimap__shade" x={mini.x + mini.w} y={0} width={Math.max(0, plotWidth - mini.x - mini.w)} height={MINIMAP_H} />
                  </>
                ) : null}
                <rect className="wtl-minimap__window" data-zoomed={zoomed || undefined} x={mini.x + 0.5} y={1.5} width={Math.max(4, mini.w - 1)} height={MINIMAP_H - 3} rx={4} />
              </svg>
            </div>
          ) : null}

          {hoverRun && hover && !(detailsOpen && selected === hoverRun.id) ? (
            <div className="wtl-tip" style={{ left: Math.max(8, Math.min(chartWidth - 296, hover.x + 12)), top: hover.y + 14 }} role="tooltip" data-wtl-tip>
              <div className="font-semibold text-foreground">{runDetails(hoverRun, now).title}</div>
              <div className="text-muted">
                {hoverRun.agent} · {runDetails(hoverRun, now).trigger}
              </div>
              <div className="tabular-nums text-muted">
                {runDetails(hoverRun, now).outcome} · {runDetails(hoverRun, now).duration} · {runDetails(hoverRun, now).tokens}
              </div>
              {/* Its real times: a short run is drawn wider than it lasted (MIN_BAR_PX). */}
              <div className="tabular-nums text-muted">{runDetails(hoverRun, now).when}</div>
            </div>
          ) : null}

          {overlay.layer}

          {selectedRun && detailsOpen && anchor ? <Details key={selectedRun.id} run={selectedRun} now={now} left={anchor.left} top={anchor.top} hideOpen={hideOpen} onClose={() => setDetailsOpen(false)} /> : null}
        </div>

        <div className="wtl-footer" data-wtl-footer>
          <span>{footerText(totals.runs, from, to, now)}</span>
          {error && !empty ? <span className="text-danger">{error}</span> : null}
          {onRange && !compact ? <RangeControl from={from} to={to} now={now} preset={preset} onRange={onRange} /> : null}
        </div>
      </div>
    </section>
  );
}
